import useSWR from 'swr';
import { customSWRConfig, customSWRResponse, DetailedUser, IEmail, IPhone } from '../model/types';
import { isValidNumberForRequest } from '../utils/validation';
import useSWRCacheKeys from './useSWRCacheKeys';

export type CustomizedDetailedUser = Omit<DetailedUser, 'email' | 'emails' | 'phone' | 'phones'> & {
    primaryEmail: IEmail;
    primaryPhone: IPhone;
    secondaryEmails: IEmail[];
    secondaryPhones: IPhone[];
};

const isPrimaryEmail = (primaryEmail: string) => (email: IEmail) => email.email === primaryEmail;

const isPrimaryPhone = (primaryPhone: string) => (phone: IPhone) => phone.phone === primaryPhone;

function deriveCustomizedDetailedUser(user: DetailedUser) {
    if (!user) return undefined;

    const { email, emails, phone, phones, ...rest } = user;

    const primaryEmail = emails.find(isPrimaryEmail(email));
    const primaryPhone = phones.find(isPrimaryPhone(phone));
    const secondaryEmails = emails.filter((emailItem) => emailItem.email !== email);
    const secondaryPhones = phones.filter((phoneItem) => phoneItem.phone !== phone);

    return {
        ...rest,
        primaryEmail,
        primaryPhone,
        secondaryEmails,
        secondaryPhones
    };
}

export default function useUserById(userId: string, config: customSWRConfig = {}): customSWRResponse<DetailedUser> {
    const isRequestAvailable = isValidNumberForRequest(userId);
    const keys = useSWRCacheKeys(isRequestAvailable ? `/users/${userId}` : null);

    return useSWR<DetailedUser>(keys, { ...config });
}

export { deriveCustomizedDetailedUser };
